import React, { useState, ChangeEvent, useEffect, useRef, Dispatch } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    TextField,
    Grid,
    Typography,
} from '@mui/material';
import { CustomerForm, NewFmtpCustomer } from './types';
import { editCustomer } from 'src/api/customer';
import { FmtpError, FmtpErrorCode } from '@lanterds/types';
import { SnackbarKey, useSnackbar } from 'notistack';

interface CreateCustomerDialogProps {
    open: boolean;
    onClose: () => void;
    onCustomerCreated: () => void;
    customerInfo?: CustomerForm | null;
    isEditing: boolean;
    setIsSavingCustomer: Dispatch<React.SetStateAction<boolean>>;
}

const customerFieldMap: { [key: string]: string } = {
    name: 'Name',
    fmtpId: 'FMTP ID',
    d365Id: 'CRM / D365 ID',
    lt1CustomerCode: 'LT1 Customer Code',
    lt1Id: 'LT1 ID',
};

const blankCustomerForm: CustomerForm = {
    name: '',
    fmtpId: '',
    lt1CustomerCode: '',
    lt1Id: '',
    d365Id: '',
};

export default function EditCustomerDialog({
    open,
    onClose,
    onCustomerCreated,
    customerInfo,
    isEditing,
    setIsSavingCustomer,
}: CreateCustomerDialogProps) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [customerForm, setCustomerForm] = useState<CustomerForm>(blankCustomerForm);
    const [formErrors, setFormErrors] = useState<Partial<CustomerForm>>({});
    const [customerExistsMessage, setCustomerExistsMessage] = useState('');
    const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
    const snackbarRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        if (customerInfo) {
            setCustomerForm(customerInfo);
        }
        setFormErrors({});
        setCustomerExistsMessage('');
    }, [open, customerInfo]);

    const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setCustomerForm({ ...customerForm, [name]: value });
    };

    const validateFields = (): Partial<CustomerForm> => {
        const tempFormErrors: Partial<CustomerForm> = {};

        if (!customerForm.name) {
            tempFormErrors.name = 'Name cannot be blank.';
        }

        if (!customerForm.fmtpId) {
            tempFormErrors.fmtpId = 'FMTP ID cannot be blank.';
        }

        if (!customerForm.d365Id) {
            tempFormErrors.d365Id = 'CRM / D365 ID cannot be blank.';
        }

        if (customerForm.lt1Id) {
            // This regex checks whether the value contains only digits
            if (/[^0-9]/.test(customerForm.lt1Id)) {
                tempFormErrors.lt1Id = 'LT1 ID must only contain numbers.';
            }
        }

        setCustomerExistsMessage('');
        setFormErrors(tempFormErrors);
        return tempFormErrors;
    };

    const mapErrorFields = (message: string) => {
        let words = message.split(' ');
        words = words.map((word: string) => {
            if (word in customerFieldMap) {
                return customerFieldMap[word];
            } else {
                return word;
            }
        });

        return words.join(' ');
    };

    const handleSave = async () => {
        const errors = validateFields();
        if (!Object.keys(errors).length) {
            setIsSavingCustomer(true);
            setIsCreatingCustomer(true);

            snackbarRef.current = enqueueSnackbar(`${!isEditing ? 'Creating New' : 'Editing'} Customer...`, {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });

            try {
                const { fmtpId, name, d365Id, lt1CustomerCode, lt1Id } = customerForm;

                const customerInfo: NewFmtpCustomer = {
                    name: name.trim(),
                    fmtpId: fmtpId.trim(),
                    d365Id: d365Id.trim(),
                    lt1CustomerCode: lt1CustomerCode ? lt1CustomerCode.trim() : null,
                    lt1Id: lt1Id ? Number(lt1Id.toString().trim()) : null,
                };

                const result = await editCustomer(customerInfo, !isEditing);

                closeSnackbar(snackbarRef.current);

                if (result.statusCode === 409) {
                    const mappedMessage = mapErrorFields(result.message);
                    setCustomerExistsMessage(mappedMessage);
                    closeSnackbar(snackbarRef.current);
                    setIsCreatingCustomer(false);
                    return;
                }

                snackbarRef.current = enqueueSnackbar(`Customer Successfully ${!isEditing ? 'Created' : 'Edited'}!`, {
                    variant: 'success',
                    preventDuplicate: true,
                    disableWindowBlurListener: true,
                    action: (key) => (
                        <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                            Dismiss
                        </Button>
                    ),
                });

                snackbarRef.current = enqueueSnackbar(
                    'Please wait 5 minutes for customer list changes to take effect in all services.',
                    {
                        variant: 'info',
                        preventDuplicate: true,
                        disableWindowBlurListener: true,
                        autoHideDuration: null,
                        action: (key) => (
                            <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                                Dismiss
                            </Button>
                        ),
                    },
                );

                onCustomerCreated();
                setIsCreatingCustomer(false);
                setCustomerForm({ ...blankCustomerForm });
                onClose();
            } catch (err) {
                const message = `An error occurred while ${!isEditing ? 'creating' : 'editing'} the customer.`;
                const errorCode: FmtpErrorCode = 'SHU019';
                throw new FmtpError({
                    message,
                    errorCode,
                });
            }
        }
    };

    const handleCancel = () => {
        onClose();
        setCustomerForm({ ...blankCustomerForm });
    };

    return (
        <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
            <DialogTitle>{!isEditing ? 'Create New' : 'Edit'} Customer</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                required
                                id="name"
                                name="name"
                                label="Name"
                                variant="outlined"
                                value={customerForm.name}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.fmtpId}
                                helperText={formErrors.fmtpId}
                                required
                                id="fmtpId"
                                name="fmtpId"
                                label="FMTP ID"
                                variant="outlined"
                                value={customerForm.fmtpId}
                                onChange={onChangeInput}
                                autoComplete="off"
                                disabled={isEditing}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.d365Id}
                                helperText={formErrors.d365Id}
                                required
                                id="d365Id"
                                name="d365Id"
                                label="CRM / D365 ID"
                                variant="outlined"
                                value={customerForm.d365Id}
                                onChange={onChangeInput}
                                autoComplete="off"
                                disabled={isEditing}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.lt1CustomerCode}
                                helperText={formErrors.lt1CustomerCode}
                                id="lt1CustomerCode"
                                name="lt1CustomerCode"
                                label="LT1 Customer Code"
                                variant="outlined"
                                value={customerForm.lt1CustomerCode}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                error={!!formErrors.lt1Id}
                                helperText={formErrors.lt1Id}
                                id="lt1Id"
                                name="lt1Id"
                                label="LT1 ID"
                                variant="outlined"
                                value={customerForm.lt1Id}
                                onChange={onChangeInput}
                                autoComplete="off"
                            />
                        </FormControl>
                    </Grid>
                    {customerExistsMessage && (
                        <Grid item xs={12}>
                            <Typography color="error">{customerExistsMessage}</Typography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={isCreatingCustomer}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
